"use client";
import modalRegistery from "@/app/constants/modalRegistery";
import { useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/app/utils/cn";
import useModal from "@/app/lib/features/modal/modal.hook";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import { ModalSize } from "@/app/lib/features/modal/modal.types";

const Modal = () => {
  const { modals, destroyModal } = useModal();
  const { resetModal } = useProductDetail();
  // Her modal index'i için override edilebilir close handler
  const closeOverridesRef = useRef<Map<number, () => void>>(new Map());

  useEffect(() => {
    document.body.style.overflow = modals.length >= 1 ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modals.length]);

  // Modal stack küçüldüğünde stale close override'larını temizle
  useEffect(() => {
    for (const key of closeOverridesRef.current.keys()) {
      if (key >= modals.length) {
        closeOverridesRef.current.delete(key);
      }
    }
  }, [modals.length]);

  const handleModalClose = useCallback(() => {
    destroyModal();
    if (modals.length <= 1) {
      resetModal();
    }
  }, [resetModal, destroyModal, modals.length]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const lastIndex = modals.length - 1;
        const override = closeOverridesRef.current.get(lastIndex);
        if (override) override();
        else handleModalClose();
      }
    };
    if (modals.length > 0) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [modals.length, handleModalClose]);

  const getSizeClasses = (size: ModalSize) => {
    switch (size) {
      case "sm": return "sm:max-w-sm";
      case "md": return "sm:max-w-md";
      case "lg": return "sm:max-w-lg";
      case "xl": return "sm:max-w-xl";
      case "2xl": return "sm:max-w-2xl";
      case "3xl": return "sm:max-w-3xl";
      case "4xl": return "sm:max-w-4xl";
      case "5xl": return "sm:max-w-5xl";
      case "6xl": return "sm:max-w-6xl";
      case "7xl": return "sm:max-w-7xl";
      case "full": return "sm:max-w-full";
      default: return "sm:max-w-md";
    }
  };

  if (modals.length === 0) return null;

  return (
    <>
      {modals.map((modal, index) => {
        const modalComponent = modalRegistery.find((m) => m.name === modal.name);
        if (!modalComponent) return null;

        const ModalElement = modalComponent.element;
        const finalTitle = modal.title ?? modalComponent.title;
        const finalDescription = modal.description ?? modalComponent.description;
        const finalSize = modal.size ?? modalComponent.size ?? "md";
        const finalClassName = modal.className ?? modalComponent.className;
        const isFullBleedContent = finalClassName?.includes("p-0");
        // Modal kendi scroll'unu yönetiyorsa (overflow-hidden + iç scroll alanı)
        // DialogContent'e varsayılan scroll'u verme.
        const managesOwnScroll = finalClassName?.includes("overflow-hidden");

        const handleClose = () => {
          const override = closeOverridesRef.current.get(index);
          if (override) override();
          else handleModalClose();
        };

        const registerClose = (fn: () => void) => {
          closeOverridesRef.current.set(index, fn);
        };

        return (
          <Dialog key={index} open={true} onOpenChange={handleClose}>
            <DialogContent
              className={cn(
                "z-50 max-h-[85vh]",
                managesOwnScroll ? "overflow-hidden" : "overflow-y-auto",
                getSizeClasses(finalSize),
                finalClassName
              )}
            >
              <DialogHeader
                className={
                  isFullBleedContent
                    ? "shrink-0 px-6 pt-6 pb-4 border-b border-gray-100"
                    : finalTitle || finalDescription
                      ? "mb-4"
                      : ""
                }
              >
                {finalTitle ? (
                  <DialogTitle>{finalTitle}</DialogTitle>
                ) : (
                  <VisuallyHidden>
                    <DialogTitle>Modal</DialogTitle>
                  </VisuallyHidden>
                )}
                {finalDescription && (
                  <DialogDescription>{finalDescription}</DialogDescription>
                )}
              </DialogHeader>

              <ModalElement
                data={modal.data}
                onClose={handleModalClose}
                registerClose={registerClose}
              />
            </DialogContent>
          </Dialog>
        );
      })}
    </>
  );
};

export default Modal;